/**
 * calculates the maximum number of steps that we can fit into a fixed chart width, given a minimum bar width
 * @param barsPerColumn - number of bars within a single "step" (i.e. cardinality of segments)
 *                      - funnel reports with breakdowns will have multiple bars grouped together in a step
 * @param numSteps - total number of steps to attempt to fit
 * @param chartWidth
 * @param gridGap
 * @param minBarWidth - bars cannot be smaller than this width (segment bars OR entire step bars [if barsPerColumn = 1])
 *
 * @returns {
 *  numSteps - the number of steps we you should display (will likely be different than input numSteps)
 *  columnWidth - size of each column (step)
 * }
 */
export function getStepSizeToFitWidth(
  barsPerColumn: number,
  numSteps: number,
  chartWidth: number,
  gridGap: number,
  minBarWidth: number,
): {numSteps: number; columnWidth: number} {
  // calculate this iteratively because the interrelated variables involved make it difficult/complex to derive a single-pass analytical solution to this.
  for (let stepsToTry = numSteps; stepsToTry > 0; stepsToTry--) {
    const {barSize, columnWidth} = getBarSizeForNumSteps(chartWidth, barsPerColumn, stepsToTry, gridGap);

    if (barSize >= minBarWidth) {
      return {numSteps: stepsToTry, columnWidth};
    }
  }

  throw new Error(`Unable to find steps that fit within bound. Tried to fit ${numSteps} in ${chartWidth}px space.`);
}

/**
 * Calculates the column width (and associated bar width if segmented)
 * @param chartWidth
 * @param barsPerColumn - funnel reports with breakdowns will have multiple bars grouped together in a step
 * @param numSteps
 * @param gridGap
 */
export function getBarSizeForNumSteps(
  chartWidth: number,
  barsPerColumn: number,
  numSteps: number,
  gridGap: number,
): {barSize: number; columnWidth: number} {
  const gridGapSpacing = (numSteps - 1) * gridGap;
  const columnWidth = (chartWidth - gridGapSpacing) / numSteps;

  if (barsPerColumn === 1) {
    return {barSize: columnWidth, columnWidth};
  }

  // in this case bars are segmented, there are multiple bars in each step
  const barMargin = getMarginBetweenBarSegments(barsPerColumn, gridGap);
  const barGapSpacing = barMargin * (barsPerColumn - 1);

  const barSize = (columnWidth - barGapSpacing) / barsPerColumn;
  return {barSize, columnWidth};
}

/**
 * Returns the margin-left to include before each bar to prevent it touching its neighboring segment
 * 1     => 0px
 * 2..4  => 1/2 grid gap
 * 5..9  => 1/4 grid gap
 * 10..N => 2px
 */
export function getMarginBetweenBarSegments(barsPerColumn: number, gap: number): number {
  if (barsPerColumn <= 1) {
    return 0;
  }

  if (barsPerColumn >= 2 && barsPerColumn < 5) {
    return gap / 2;
  }
  if (barsPerColumn >= 5 && barsPerColumn < 10) {
    return gap / 4;
  }

  return 2;
}
